import React, { useState, useEffect, useRef } from "react";

import styled from "styled-components";

import { Checkbox } from "shared/components/Form";
import Icon from "shared/components/Icon";
import LoaderContainer from "shared/components/LoaderContainer";
import { SmallText } from "shared/components/Typography";

const PreviewContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  margin: 12px 0;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 12px;
  border-radius: 6px 6px 0 0;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.div`
  flex: 1;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PreviewContent = styled.div`
  padding: 12px;
  flex-shrink: 0;
`;

const SubjectLine = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 12px;
`;

const EmailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 200px;

  iframe {
    width: 100%;
    border: none;
    height: 100%;
    min-height: 300px;
  }

  .html-fallback {
    line-height: normal;
    font-family: inherit;
    padding: 8px;
    height: 100%;
    transform: scale(0.85);
    transform-origin: top left;
    width: 117.6%;
    overflow-x: hidden;

    /* Reset any potential inherited styles that might interfere */
    * {
      box-sizing: border-box;
    }
  }
`;

const LoadingSpinner = styled(Icon.LoadingSpinnerGreenIcon)`
  margin: 10px auto;
  display: block;
  width: 20px;
  height: 20px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 8px;
  margin: 6px 0;
  font-size: 12px;
`;

const WarningMessage = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 6px 8px;
  margin-top: 8px;
  color: #856404;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WarningIcon = styled(Icon.WarningIcon)`
  width: 12px;
  height: 12px;
  color: #856404;
  flex-shrink: 0;
`;

interface EmailPreviewData {
  applicationId: number;
  applicantName: string;
  subject: string;
  htmlBody: string;
}

interface IndividualEmailPreviewProps {
  applicantId: number;
  applicantName: string;
  previewEmail: (
    applicantId: number,
    includeAdverseAction?: boolean
  ) => Promise<EmailPreviewData | null>;
  isSelected: boolean;
  guarantorId?: number | null;
  attachmentIds?: number[];
  customMessage?: string;
  excludeAdverseActionInEmail?: boolean;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  index?: number;
}

const IndividualEmailPreview: React.FC<IndividualEmailPreviewProps> = ({
  applicantId,
  applicantName,
  previewEmail,
  isSelected,
  guarantorId,
  attachmentIds,
  customMessage,
  excludeAdverseActionInEmail = false,
  setFieldValue,
  index,
}) => {
  const [previewData, setPreviewData] = useState<EmailPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useIframe, setUseIframe] = useState(true);

  // Use the form value instead of local state
  const includeAdverseAction = !excludeAdverseActionInEmail;

  // Create a stable reference to previewEmail to prevent unnecessary re-renders
  const previewEmailRef = useRef(previewEmail);
  previewEmailRef.current = previewEmail;

  useEffect(() => {
    if (!isSelected) {
      setPreviewData(null);
      setError(null);
      return;
    }

    const loadPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await previewEmailRef.current(
          applicantId,
          includeAdverseAction
        );
        setPreviewData(data);
      } catch (err) {
        console.error("Preview error for applicant", applicantId, err);
        setError("Failed to load preview");
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(loadPreview, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [
    isSelected,
    applicantId,
    includeAdverseAction,
    guarantorId,
    attachmentIds,
    customMessage,
  ]);

  const createDataUrl = (htmlContent: string) => {
    const completeHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body {
              margin: 0 !important;
              padding: 8px !important;
              transform: scale(0.82) !important;
              transform-origin: top left !important;
              width: 122% !important;
              overflow-x: hidden !important;
            }
            * {
              font-size: inherit !important;
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;
    return `data:text/html;charset=utf-8,${encodeURIComponent(completeHtml)}`;
  };

  if (!isSelected) {
    return (
      <PreviewContainer>
        <PreviewContent>
          <SmallText style={{ color: "#6c757d", fontStyle: "italic" }}>
            Select this applicant to see email preview
          </SmallText>
        </PreviewContent>
      </PreviewContainer>
    );
  }

  // Always render the header with checkbox and warning - no loading state here
  const hasContent = previewData?.htmlBody && previewData.htmlBody.trim();

  const renderEmailContent = () => {
    if (loading) {
      return (
        <LoaderContainer loading Loader={<LoadingSpinner />}>
          <div style={{ height: 60 }} />
        </LoaderContainer>
      );
    }

    if (error) {
      return (
        <ErrorMessage>
          <SmallText>{error}</SmallText>
        </ErrorMessage>
      );
    }

    if (!previewData) {
      return (
        <SmallText style={{ color: "#6c757d", fontStyle: "italic" }}>
          No preview available
        </SmallText>
      );
    }

    return (
      <>
        <SmallText strong>Subject:</SmallText>
        <SubjectLine>
          <SmallText>{previewData.subject}</SmallText>
        </SubjectLine>

        <SmallText strong>Content:</SmallText>
        <EmailBody>
          {hasContent ? (
            useIframe ? (
              <iframe
                src={createDataUrl(previewData.htmlBody)}
                onError={() => setUseIframe(false)}
                title={`Email preview for ${applicantName}`}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="html-fallback">
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: previewData.htmlBody,
                  }}
                />
              </div>
            )
          ) : (
            <div className="html-fallback">
              <SmallText style={{ color: "#6c757d", fontStyle: "italic" }}>
                No email content available
              </SmallText>
            </div>
          )}
        </EmailBody>
      </>
    );
  };

  return (
    <PreviewContainer>
      <PreviewHeader>
        <HeaderRow>
          <HeaderTitle>
            <SmallText strong>Email Preview for {applicantName}</SmallText>
          </HeaderTitle>
          <ToggleContainer>
            <Checkbox
              id={`adverse-action-${applicantId}`}
              checked={includeAdverseAction}
              onChange={(e) => {
                const newValue = !e.target.checked;
                if (setFieldValue && typeof index === 'number') {
                  setFieldValue(`applicants.${index}.excludeAdverseActionInEmail`, newValue);
                }
              }}
              label=""
            />
            <SmallText style={{ fontSize: "11px" }}>
              Include generated adverse action letter
            </SmallText>
          </ToggleContainer>
        </HeaderRow>
        {!includeAdverseAction && (
          <WarningMessage>
            <WarningIcon />
            <span>
              You are responsible for attaching your own adverse action letter
              if you elect not to use the Vero-generated version.
            </span>
          </WarningMessage>
        )}
      </PreviewHeader>
      <PreviewContent>{renderEmailContent()}</PreviewContent>
    </PreviewContainer>
  );
};

export default IndividualEmailPreview;
